import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Heading, Image, Text } from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import appLogo from "../deepsoul_logo2.png";
import smallHappinessIcon from "../icon/small-happiness-icon.svg";
import gratitudeIcon from "../icon/gratitude-icon.svg";
import savingHappinessIcon from "../icon/saving-happiness-icon.svg";
import shareLoveIcon from "../icon/shareLove.svg";
import finalChapterIcon from "../icon/finalChapter.svg";
import lifeSummaryIcon from "../icon/lifeSummary.svg";

type PreviewId =
  | "record"
  | "emotion"
  | "memory"
  | "legacy"
  | "finalChapter"
  | "lifeSummary";

const PREVIEW_ORDER: PreviewId[] = [
  "record",
  "emotion",
  "memory",
  "legacy",
  "finalChapter",
  "lifeSummary",
];

const ROTATION_INTERVAL_MS = 6000;
const JOURNEY_SEGMENT_COUNT = PREVIEW_ORDER.length - 1;

const featureIcons: Record<PreviewId, string> = {
  record: smallHappinessIcon,
  emotion: gratitudeIcon,
  memory: savingHappinessIcon,
  legacy: shareLoveIcon,
  finalChapter: finalChapterIcon,
  lifeSummary: lifeSummaryIcon,
};

const HeroAppPreview: React.FC = () => {
  const { t } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const [activeId, setActiveId] = useState<PreviewId>("record");
  const [cycleKey, setCycleKey] = useState(0);
  const [isPageVisible, setIsPageVisible] = useState(
    () => typeof document === "undefined" || !document.hidden
  );
  const journeyRef = useRef<HTMLDivElement>(null);
  const stageRefs = useRef<Partial<Record<PreviewId, HTMLButtonElement | null>>>({});
  const cycleIdentityRef = useRef("");
  const remainingTimeRef = useRef(ROTATION_INTERVAL_MS);
  const timerRef = useRef<number | null>(null);

  const activeIndex = PREVIEW_ORDER.indexOf(activeId);
  const formattedProgress = `${String(activeIndex + 1).padStart(2, "0")} / ${String(
    PREVIEW_ORDER.length
  ).padStart(2, "0")}`;

  useEffect(() => {
    const handleVisibilityChange = () => setIsPageVisible(!document.hidden);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const cycleIdentity = `${activeId}-${cycleKey}`;

    if (cycleIdentityRef.current !== cycleIdentity) {
      cycleIdentityRef.current = cycleIdentity;
      remainingTimeRef.current = ROTATION_INTERVAL_MS;
    }

    if (shouldReduceMotion || !isPageVisible) {
      return undefined;
    }

    const startedAt = Date.now();
    const timer = window.setTimeout(() => {
      timerRef.current = null;
      setActiveId((currentId) => {
        const currentIndex = PREVIEW_ORDER.indexOf(currentId);
        return PREVIEW_ORDER[(currentIndex + 1) % PREVIEW_ORDER.length];
      });
      setCycleKey((currentKey) => currentKey + 1);
    }, remainingTimeRef.current);
    timerRef.current = timer;

    return () => {
      window.clearTimeout(timer);
      if (timerRef.current === timer) {
        timerRef.current = null;
      }
      const elapsed = Date.now() - startedAt;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    };
  }, [activeId, cycleKey, isPageVisible, shouldReduceMotion]);

  useEffect(() => {
    const journey = journeyRef.current;
    const activeStage = stageRefs.current[activeId];

    if (!journey || !activeStage || journey.scrollWidth <= journey.clientWidth) {
      return;
    }

    const targetLeft =
      activeStage.offsetLeft - journey.clientWidth / 2 + activeStage.offsetWidth / 2;

    journey.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: shouldReduceMotion ? "auto" : "smooth",
    });
  }, [activeId, shouldReduceMotion]);

  const handleStageSelect = (id: PreviewId) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setActiveId(id);
    setCycleKey((currentKey) => currentKey + 1);
  };

  return (
    <Box
      data-testid="hero-app-preview"
      position="relative"
      height="100%"
      p={{ base: 4, sm: 5, md: 6, lg: 7 }}
      border="1px solid rgba(255, 255, 255, 0.84)"
      borderRadius={{ base: "24px", md: "32px" }}
      bg="linear-gradient(145deg, rgba(255,255,255,0.9), rgba(255,244,246,0.8) 62%, rgba(235,246,255,0.86))"
      boxShadow="0 22px 54px rgba(50, 54, 67, 0.12)"
      backdropFilter="blur(18px)"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="-100px"
        right="-60px"
        width="280px"
        height="280px"
        borderRadius="full"
        bg="rgba(255, 174, 184, 0.18)"
        filter="blur(10px)"
        pointerEvents="none"
      />

      <Text
        position="relative"
        mb={1}
        color="rgba(37, 39, 45, 0.58)"
        fontSize={{ base: "sm", md: "md" }}
        fontWeight="800"
      >
        {t("hero.demo.greeting")}
      </Text>
      <Text
        position="relative"
        mb={{ base: 5, md: 7 }}
        color="#4F5058"
        fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
        fontWeight="900"
        lineHeight={1.4}
      >
        {t(`hero.demo.${activeId}.greeting`)}
      </Text>

      <Box
        ref={journeyRef}
        role="group"
        aria-label={t("hero.demo.controlsLabel")}
        position="relative"
        mx={{ base: -4, sm: -5, md: 0 }}
        px={{ base: 4, sm: 5, md: 0 }}
        pt={2}
        pb={{ base: 5, md: 6 }}
        overflowX={{ base: "auto", md: "visible" }}
        overflowY="hidden"
        scrollSnapType={{ base: "x proximity", md: "none" }}
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box
          position="relative"
          display="grid"
          gridTemplateColumns={{ base: "repeat(6, 96px)", md: "repeat(6, minmax(0, 1fr))" }}
          gap={{ base: 3, md: 2, lg: 3 }}
          minW={{ base: "660px", md: 0 }}
        >
          <Box
            aria-hidden="true"
            position="absolute"
            top={{ base: "34px", md: "38px" }}
            left={{ base: "48px", md: "8.333%" }}
            right={{ base: "48px", md: "8.333%" }}
            height="2px"
            bg="linear-gradient(90deg, rgba(255,99,112,0.34), rgba(135,190,238,0.34))"
          />

          <Box
            aria-hidden="true"
            position="absolute"
            top={{ base: "34px", md: "38px" }}
            left={{ base: "48px", md: "8.333%" }}
            right={{ base: "48px", md: "8.333%" }}
            display="grid"
            gridTemplateColumns={`repeat(${JOURNEY_SEGMENT_COUNT}, minmax(0, 1fr))`}
            height="3px"
          >
            {Array.from({ length: JOURNEY_SEGMENT_COUNT }, (_, segmentIndex) => {
              const isCompleted = segmentIndex < activeIndex;
              const isActiveSegment =
                segmentIndex === activeIndex && activeIndex < JOURNEY_SEGMENT_COUNT;
              const segmentState = isCompleted
                ? "completed"
                : isActiveSegment
                ? "active"
                : "pending";

              return (
                <Box
                  key={`${cycleKey}-${segmentIndex}`}
                  data-testid={`hero-progress-segment-${segmentIndex + 1}`}
                  data-state={segmentState}
                  height="100%"
                  overflow="hidden"
                >
                  <Box
                    height="100%"
                    bg="#FF4F5F"
                    transform={isCompleted ? "scaleX(1)" : "scaleX(0)"}
                    transformOrigin="left center"
                    animation={
                      isActiveSegment && !shouldReduceMotion
                        ? `heroJourneyProgress ${ROTATION_INTERVAL_MS}ms linear forwards`
                        : undefined
                    }
                    sx={{
                      animationPlayState: isPageVisible ? "running" : "paused",
                      "@keyframes heroJourneyProgress": {
                        from: { transform: "scaleX(0)" },
                        to: { transform: "scaleX(1)" },
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {PREVIEW_ORDER.map((id) => {
            const isActive = id === activeId;

            return (
              <Button
                ref={(node) => {
                  stageRefs.current[id] = node;
                }}
                key={id}
                type="button"
                aria-pressed={isActive}
                data-preview-id={id}
                onClick={() => handleStageSelect(id)}
                position="relative"
                zIndex={1}
                width="100%"
                height="auto"
                minH={{ base: "92px", md: "104px" }}
                p={0}
                border="0"
                borderRadius="0"
                bg="transparent"
                color="#4A4C54"
                flexDirection="column"
                justifyContent="flex-start"
                gap={{ base: 2, md: 2.5 }}
                fontSize={{ base: "xs", lg: "sm" }}
                fontWeight="800"
                lineHeight={1.2}
                whiteSpace="normal"
                scrollSnapAlign="center"
                _hover={{ color: "#4A4C54" }}
                _active={{ bg: "transparent", color: "#4A4C54" }}
                _focusVisible={{
                  outline: "3px solid rgba(37, 39, 45, 0.7)",
                  outlineOffset: "4px",
                }}
              >
                <Flex
                  as="span"
                  aria-hidden="true"
                  align="center"
                  justify="center"
                  width={{ base: "68px", md: "76px" }}
                  height={{ base: "68px", md: "76px" }}
                  border="2px solid"
                  borderColor={
                    isActive ? "rgba(37,39,45,0.18)" : "rgba(255,255,255,0.94)"
                  }
                  borderRadius="full"
                  bg="rgba(255,255,255,0.92)"
                  boxShadow={
                    isActive
                      ? "0 0 0 6px rgba(255,255,255,0.46), 0 12px 28px rgba(86,88,98,0.13)"
                      : "0 8px 22px rgba(86,88,98,0.08)"
                  }
                  transform={isActive ? "scale(1.08)" : "scale(1)"}
                  transition="transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease"
                >
                  <Image
                    src={featureIcons[id]}
                    alt=""
                    width={{ base: "27px", md: "31px" }}
                    height={{ base: "27px", md: "31px" }}
                    objectFit="contain"
                  />
                </Flex>
                <Box as="span">{t(`hero.demo.${id}.label`)}</Box>
              </Button>
            );
          })}
        </Box>
      </Box>

      <Box
        key={activeId}
        aria-live="polite"
        position="relative"
        display="grid"
        gridTemplateColumns={{ base: "auto minmax(0, 1fr)", sm: "auto minmax(0, 1fr) auto" }}
        alignItems="center"
        gap={{ base: 3, sm: 5, md: 6 }}
        minH={{ base: "150px", md: "128px" }}
        px={{ base: 4, sm: 5, md: 7 }}
        py={{ base: 4, md: 5 }}
        border="1px solid rgba(255,255,255,0.9)"
        borderRadius={{ base: "20px", md: "24px" }}
        bg="linear-gradient(105deg, rgba(255,240,243,0.72), rgba(255,255,255,0.94) 48%, rgba(232,245,255,0.78))"
        boxShadow="0 14px 34px rgba(85,88,100,0.08)"
        overflow="hidden"
        animation={shouldReduceMotion ? undefined : "heroPreviewIn 220ms ease-out"}
        sx={{
          "@keyframes heroPreviewIn": {
            from: { opacity: 0, transform: "translateY(5px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Text
          data-testid="hero-preview-progress"
          color="#E62F3E"
          fontSize={{ base: "lg", md: "2xl" }}
          fontWeight="900"
          letterSpacing="0.04em"
          whiteSpace="nowrap"
        >
          {formattedProgress}
        </Text>

        <Box
          minW={0}
          pl={{ base: 3, md: 6 }}
          borderLeft="1px solid rgba(37,39,45,0.16)"
        >
          <Heading
            as="h2"
            mb={1.5}
            color="#EF2F3F"
            fontSize={{ base: "md", md: "xl" }}
            fontWeight="900"
          >
            {t(`hero.demo.${activeId}.title`)}
          </Heading>
          <Text
            color="rgba(37, 39, 45, 0.7)"
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="700"
            lineHeight={1.65}
          >
            {t(`hero.demo.${activeId}.message`)}
          </Text>
        </Box>

        <Image
          data-testid="hero-message-logo"
          src={appLogo}
          alt=""
          aria-hidden="true"
          display={{ base: "none", sm: "block" }}
          width={{ sm: "48px", md: "58px" }}
          height={{ sm: "48px", md: "58px" }}
          borderRadius="full"
          objectFit="cover"
          boxShadow="0 10px 24px rgba(232, 44, 40, 0.2)"
        />
      </Box>
    </Box>
  );
};

export default HeroAppPreview;
